import {
  BulkMessagePriorityMassMessageFilter,
  BulkMessageStatus,
  BulkMessagesService,
  OnlineMassMessagesService,
  PageResponseOfBulkMessagesDto,
} from '@api'
import { SortingState } from '@tanstack/react-table'

import { apiClientV2 } from '@/utils/axios'

import type {
  BulkMessage,
  BulkMessageResponseContent,
  MassMessageCreateRequest,
  MassMessagesFormValues,
} from '../types'
import { BulkMessageWithdrawResponse } from '../types'

interface MassMessagesFilters {
  includeSendCount?: boolean
  includeStatistic?: boolean
  priorityMassMessageFilter: BulkMessagePriorityMassMessageFilter
  status?: BulkMessageStatus
}

export interface FetchProps {
  creatorId: number | undefined
  page: number
  size: number
  sortOption?: SortingState
  filters?: MassMessagesFilters
}

export type BulkMessagePageResponse = Omit<
  PageResponseOfBulkMessagesDto,
  'content'
> & {
  content: Array<BulkMessageResponseContent>
}
const fetchMassMessages = async ({
  creatorId,
  page,
  size,
  sortOption,
  filters = {
    priorityMassMessageFilter: BulkMessagePriorityMassMessageFilter.ONLY_OMM,
  },
}: FetchProps) => {
  const response = await BulkMessagesService.queryBulkMessagesV2({
    creatorId: creatorId as number,
    page,
    size,
    sort:
      sortOption?.map((sort) => `${sort.id},${sort.desc ? 'desc' : 'asc'}`) ??
      [],
    priorityMassMessageFilter: filters.priorityMassMessageFilter,
    status: filters?.status ?? undefined,
  })

  // we can't use current Message Type in FE
  return response as unknown as BulkMessagePageResponse
}

const getMassMessageById = async ({
  creatorId,
  messageId,
}: {
  creatorId: number | undefined
  messageId: string | undefined
}) => {
  const response = await apiClientV2.get<BulkMessageResponseContent>(
    `/bulk-messages/${creatorId}/${messageId}`,
  )

  return response.data
}

const updateMassMessage = async ({
  messageId,
  creatorId,
  requestBody,
}: {
  messageId: number | undefined
  creatorId: number | undefined
  requestBody: Partial<MassMessageCreateRequest> & {
    status?: BulkMessage['status']
  }
}) => {
  const { followUpDto, ...rest } = requestBody

  const response = await apiClientV2.put<MassMessagesFormValues>(
    `/bulk-messages/${creatorId}/${messageId}`,
    {
      followUpDto,
      message: rest,
    },
  )

  return response.data
}

const createMassMessage = async ({
  creatorId,
  requestBody,
}: {
  creatorId: number | undefined
  requestBody: MassMessageCreateRequest
}) => {
  const { followUpDto, ...rest } = requestBody
  const response = await apiClientV2.post<MassMessagesFormValues>(
    `/bulk-messages/${creatorId}`,
    {
      followUpDto,
      message: rest,
    },
  )

  return response.data
}

const withdrawBulkMessage = async ({
  creatorId,
  messageId,
}: {
  creatorId: number | undefined
  messageId: number
}) => {
  return apiClientV2
    .post(`/bulk-messages/${creatorId}/${messageId}/withdraw`)
    .then((response) => BulkMessageWithdrawResponse.parse(response.data))
}

const setMultipleBulkMessageState = async ({
  creatorId,
  state,
}: {
  creatorId: number | undefined
  state: boolean
}) => {
  return apiClientV2.post(`/bulk-messages/${creatorId}/allow-multiple`, {
    state,
  })
}

const setGlobalBulkMessageCooldown = async ({
  creatorId,
  cooldown,
}: {
  creatorId: number | undefined
  cooldown: number
}) => {
  return apiClientV2.post(`/bulk-messages/${creatorId}/global-cooldown`, {
    cooldown,
  })
}

const unsendMassMessage = (
  creatorId: number,
  massMessageId: BulkMessage['id'],
): Promise<void> => {
  return apiClientV2.put(`/bulk-messages/${creatorId}/${massMessageId}`, {
    message: { status: 'Cancelled' },
  })
}

const getMassMessageStatisticsByMessageId = async ({
  creatorId,
  messageId,
}: {
  creatorId: number
  messageId: number
}) => {
  return OnlineMassMessagesService.getBulkMessageStatisticsV2({
    creatorId,
    messageId,
  })
}

export const massMessageService = {
  fetchMassMessages,
  getMassMessageById,
  updateMassMessage,
  createMassMessage,
  withdrawBulkMessage,
  setMultipleBulkMessageState,
  setGlobalBulkMessageCooldown,
  unsendMassMessage,
  getMassMessageStatisticsByMessageId,
}
